import type { ReactNode } from 'react';

import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import legacyTheme from '../../../theme';
import { tokens } from '../../../theme-next';
import type { TaxData } from '../../../types/cg';
import { useFormCGIAStore } from '../store/useFormCGIAStore';
import { SectionCard } from './PremiumComponents';

/**
 * Étape 2 du flow FormCGIA v2 (inversion des étapes — voir plan).
 *
 * Confirmation des coûts AVANT le dépôt des pièces. Reprend strictement
 * le contenu de l'ancienne `TaxConfirmationDialog` (calculs, wording des
 * notes, breakdown taxes + prestation), mais habillé en étape plein
 * écran avec la même structure visuelle que `PlateAndDepartmentScreen`
 * (overline jaune + titre 38 px + SectionCard à liseré).
 *
 * Avoir ce contenu en modale n'avait pas de sens dans le nouveau flow
 * d'inversion : c'est une étape obligatoire, pas un overlay.
 */

interface TaxConfirmationScreenProps {
  /** Frais SKIPCAR dynamique (useClientFees / useAgencyFees côté parent). */
  prestationHT: number;
  /**
   * Appelé quand l'agence valide un tarif RECALCULÉ après modification de la
   * plaque/département à l'étape de vérification (`taxConfirmReturn === 'final'`).
   * Le parent ouvre alors la certification puis la génération de facture.
   */
  onAcceptFinal?: () => void;
}

export const TaxConfirmationScreen = ({ prestationHT, onAcceptFinal }: TaxConfirmationScreenProps) => {
  const taxData = useFormCGIAStore((s) => s.taxData) as TaxData | null;
  const setV2Step = useFormCGIAStore((s) => s.setV2Step);
  const taxConfirmReturn = useFormCGIAStore((s) => s.taxConfirmReturn);
  const setTaxConfirmReturn = useFormCGIAStore((s) => s.setTaxConfirmReturn);

  // Retour depuis l'étape de vérification après recalcul ECG : on valide un
  // nouveau tarif, pas le devis initial avant dépôt.
  const isFinalReturn = taxConfirmReturn === 'final';

  const totalHT = taxData ? taxData.taxeTotale + prestationHT : 0;
  const totalTTC = taxData ? taxData.taxeTotale + prestationHT * 1.2 : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <Box>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: tokens.color.yellow,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          {isFinalReturn ? 'Recalcul du tarif · Plaque ou département modifié' : 'Étape 2 / 3 · Confirmation des coûts'}
        </Typography>
        <Typography
          sx={{
            fontSize: '38px',
            fontWeight: 900,
            color: tokens.color.dark,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
            marginTop: '6px',
          }}
        >
          {isFinalReturn ? 'Validez le nouveau tarif pour finaliser' : 'Relisez le devis avant de déposer les pièces'}
        </Typography>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 500,
            color: tokens.color.dark,
            marginTop: '10px',
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          {isFinalReturn
            ? "La plaque ou le département ont été modifiés à l'étape de vérification : voici le tarif recalculé par MiSIV. En cas d'accord, la demande sera finalisée."
            : "Voici l'estimation calculée par MiSIV à partir de la plaque et du département saisis. En cas d'accord, l'étape suivante demandera le dépôt des documents."}
        </Typography>
      </Box>

      {/* ── Détail des coûts ───────────────────────────────── */}
      <SectionCard
        title="Détail du devis"
        subtitle="Taxe MiSIV + prestation SKIPCAR"
        subtitleStrong
        accentColor={tokens.color.yellow}
      >
        <Box sx={{ padding: '24px 26px' }}>
          {/* Total en gros, encadré jaune */}
          <Box
            sx={{
              backgroundColor: tokens.color.yellowLight,
              border: `2px solid ${tokens.color.yellow}`,
              borderRadius: `${tokens.radius['2xl']}px`,
              padding: '24px 20px',
              marginBottom: '24px',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '13px', color: tokens.color.textSecondary, marginBottom: '6px' }}>
              Ce changement de titulaire devrait vous être facturé :
            </Typography>
            <Typography
              sx={{
                fontSize: '42px',
                fontWeight: 900,
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
                color: tokens.color.dark,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {totalHT.toFixed(2)} €{' '}
              <Typography component="span" sx={{ fontSize: '20px', fontWeight: 700 }}>
                HT
              </Typography>
            </Typography>
          </Box>

          {/* Breakdown */}
          <Stack spacing={0}>
            <Row label="Changement de titulaire — taxes" value={`${(taxData?.taxeTotale ?? 0).toFixed(2)} € HT`} />
            <Row label="Prestation SKIPCAR" value={`${prestationHT.toFixed(2)} € HT`} />
            <Row
              label="Total"
              strong
              valueBlock={
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '15px', fontWeight: 800, color: tokens.color.dark }}>
                    {totalHT.toFixed(2)} € HT
                  </Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 800, color: tokens.color.dark }}>
                    {totalTTC.toFixed(2)} € TTC
                  </Typography>
                </Box>
              }
            />
          </Stack>

          <Divider sx={{ marginY: '20px' }} />

          {/* Notes obligatoires (wording legacy strict) */}
          <Stack spacing={1.25}>
            <NoteAlert severity="info" icon="📝">
              Le résultat affiché est une estimation d'après les informations que vous avez fournies.
            </NoteAlert>
            <NoteAlert severity="info" icon="🏷️" italic>
              Le montant définitif de la carte grise sera en effet fixé par le service chargé des immatriculations.
            </NoteAlert>
            <NoteAlert severity="warning" icon="💵" italic>
              En cas de variation entre l'estimation et le prix final vous vous engagez à régler la différence.
            </NoteAlert>
            <NoteAlert severity="info" icon="📜" italic>
              La délivrance d'un certificat d'immatriculation (ex-carte grise) est soumise au paiement de 4 taxes et
              d'une redevance.
            </NoteAlert>
            <NoteAlert severity="info" icon="🚫" italic>
              SKIPCAR ne surfacture aucunement les taxes et redevance, le prix des taxes de la carte grise est fixe.
            </NoteAlert>
          </Stack>
        </Box>
      </SectionCard>

      {/* ── CTAs ────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <ThemeProvider theme={legacyTheme}>
          <Button
            className="secondaryDealActionButton"
            onClick={() => {
              if (isFinalReturn) {
                setTaxConfirmReturn('ingesting');
                setV2Step('verify');
              } else {
                setV2Step('plate_and_dept');
              }
            }}
          >
            {isFinalReturn ? '← Retour à la vérification' : '← Modifier la plaque'}
          </Button>
          <Button
            className="dealActionButton"
            onClick={() => {
              if (isFinalReturn) {
                setTaxConfirmReturn('ingesting');
                onAcceptFinal?.();
              } else {
                setV2Step('ingesting');
              }
            }}
            disabled={!taxData}
            color="primary"
            variant="contained"
          >
            {isFinalReturn ? 'Valider le nouveau tarif →' : "Je suis d'accord — déposer les pièces →"}
          </Button>
        </ThemeProvider>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
//  Helpers internes (alignés avec TaxConfirmationDialog)
// ─────────────────────────────────────────────────────────────

const Row = ({
  label,
  value,
  valueBlock,
  strong,
}: {
  label: string;
  value?: string;
  valueBlock?: ReactNode;
  strong?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: strong ? 'flex-start' : 'center',
      paddingY: '12px',
      borderBottom: strong ? 'none' : `1px solid ${tokens.color.border}`,
    }}
  >
    <Typography sx={{ fontSize: '14px', fontWeight: strong ? 800 : 500, color: tokens.color.dark }}>{label}</Typography>
    {valueBlock ?? (
      <Typography sx={{ fontSize: '14px', fontWeight: 800, color: tokens.color.dark }}>{value}</Typography>
    )}
  </Box>
);

const NoteAlert = ({
  severity,
  icon,
  italic,
  children,
}: {
  severity: 'info' | 'warning';
  icon: string;
  italic?: boolean;
  children: ReactNode;
}) => {
  const iconBg = severity === 'warning' ? tokens.color.orangeLight : tokens.color.yellowLight;
  const iconBorder = severity === 'warning' ? tokens.color.orangeBorder : '#F3E8B8';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '10px 12px',
        backgroundColor: tokens.color.surface,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: `${tokens.radius.lg}px`,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          backgroundColor: iconBg,
          border: `1px solid ${iconBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '15px',
          flexShrink: 0,
          marginTop: '1px',
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '13.5px',
          lineHeight: 1.5,
          color: tokens.color.dark,
          fontStyle: italic ? 'italic' : 'normal',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};

export default TaxConfirmationScreen;
